import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import { CustomersContext, customersInitialState, IFilterValues, TTypeOfModalWindow } from './customersContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { TCustomerFullInfoVisibleForm } from '../../CustomerFullInfo/customerFullInfoState/customerFullInfoContext';
import {
	TCountOfItems,
	TCustomersSortBy,
	ICustomers,
	IGetPaginatedCustomers,
	TTypeOfServiceProvided,
	EntityStatus,
} from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { AxiosResponse } from 'axios';
import { customersReducer } from './customersReducer';
import { CustomersActionTypes } from './customersActions';
import { getPaginatedCustomers, deleteCustomer, archiveCustomer, restoreCustomer } from 'backend/customers';

const CustomersState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(customersReducer, customersInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<ICustomers>, IGetPaginatedCustomers>(
		isMounted.current ? getPaginatedCustomers : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			clientType: state.selectedClientType,
			status: state.status,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
			sortByRestHotel:
				state.sortBy === 'Rest/Hotel1-10' || state.sortBy === 'Rest/Hotel10-1' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: CustomersActionTypes.SET_CUSTOMERS,
				payload: {
					customers: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = async () => {
		try {
			showLoader();
			await deleteCustomer(state.IDForDeletion);
			const filteredCustomers = state.customers.customers.filter((el) => el.id !== state.IDForDeletion);
			dispatch({
				type: CustomersActionTypes.SET_CUSTOMERS,
				payload: {
					customers: { customerCount: state.customers.customerCount - 1, customers: filteredCustomers },
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: CustomersActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const archiveSelectedItem = async () => {
		try {
			showLoader();
			await archiveCustomer(state.IDForArchiving);
			const filteredCustomers = state.customers.customers.filter((el) => el.id !== state.IDForArchiving);
			dispatch({
				type: CustomersActionTypes.SET_CUSTOMERS,
				payload: {
					customers: { customerCount: state.customers.customerCount - 1, customers: filteredCustomers },
					typeOfModalWindow: 'archiveSuccess',
				},
			});
		} catch (err) {
			dispatch({
				type: CustomersActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'archiveError',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const restoreSelectedItem = async () => {
		try {
			showLoader();
			await restoreCustomer(state.IDForRestoring);
			const filteredCustomers = state.customers.customers.filter((el) => el.id !== state.IDForRestoring);
			dispatch({
				type: CustomersActionTypes.SET_CUSTOMERS,
				payload: {
					customers: { customerCount: state.customers.customerCount - 1, customers: filteredCustomers },
					typeOfModalWindow: 'restoreSuccess',
				},
			});
		} catch (err) {
			dispatch({
				type: CustomersActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'restoreError',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: CustomersActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const setClientType = (value: Nullable<TTypeOfServiceProvided>) => {
		dispatch({
			type: CustomersActionTypes.SET_CLIENT_TYPE,
			payload: {
				selectedClientType: value,
			},
		});
	};

	const setStatus = (value: EntityStatus) => {
		dispatch({
			type: CustomersActionTypes.SET_STATUS,
			payload: {
				status: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: CustomersActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TCustomersSortBy) => {
		dispatch({
			type: CustomersActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TCountOfItems) => {
		dispatch({
			type: CustomersActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: CustomersActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: CustomersActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const setIDForArchiving = (value: string) => {
		dispatch({
			type: CustomersActionTypes.SET_ID_FOR_ARCHIVING,
			payload: {
				IDForArchiving: value,
				typeOfModalWindow: 'toConfirmArchiveItem',
			},
		});
	};

	const setIDForRestoring = (value: string) => {
		dispatch({
			type: CustomersActionTypes.SET_ID_FOR_RESTORING,
			payload: {
				IDForRestoring: value,
				typeOfModalWindow: 'toConfirmRestoreItem',
			},
		});
	};

	const setFullInfoData = (value: string, activeTab?: TCustomerFullInfoVisibleForm) => {
		dispatch({
			type: CustomersActionTypes.SET_FULL_INFO_DATA,
			payload: {
				fullInfoData: {
					id: value,
					activeTab,
				},
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: CustomersActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: CustomersActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
		isMounted.current = true;
		refetch();
	};

	return (
		<CustomersContext.Provider
			value={{
				deleteSelectedItem,
				archiveSelectedItem,
				restoreSelectedItem,
				changeSearchString,
				setClientType,
				setStatus,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				setIDForArchiving,
				setIDForRestoring,
				setFullInfoData,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				customers: state.customers,
				searchString: state.searchString,
				selectedClientType: state.selectedClientType,
				status: state.status,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				IDForArchiving: state.IDForArchiving,
				IDForRestoring: state.IDForRestoring,
				fullInfoData: state.fullInfoData,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</CustomersContext.Provider>
	);
};

export default CustomersState;
